
import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { pb } from '@/lib/pocketbase';

export const useInstantSession = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);

  const createInstantSession = async () => {
    if (!user || user.role !== 'tutor') {
      toast({
        title: "Error",
        description: "Only tutors can create instant sessions",
        variant: "destructive"
      });
      return null;
    }

    // Check if tutor is approved for instant sessions
    try {
      const tutorProfile = await pb.collection('tutors').getFirstListItem(
        `user = "${user.id}"`
      );
      
      if (!tutorProfile.instant_session_approved) {
        toast({
          title: "Not Approved",
          description: "Your instant session feature is pending admin approval",
          variant: "destructive"
        });
        return null;
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to verify your instant session permissions",
        variant: "destructive"
      });
      return null;
    }

    setIsCreating(true);
    try {
      // Generate a unique session ID
      const sessionId = 'instant-' + Date.now() + '-' + Math.random().toString(36).substring(2, 9);
      
      console.log('Creating instant session with ID:', sessionId);

      // Generate shareable link with proper domain
      const shareableLink = `${window.location.origin}/classroom?session=${sessionId}&type=instant&role=tutor`;

      // Copy to clipboard first
      try {
        await navigator.clipboard.writeText(shareableLink);
        console.log('Shareable link copied to clipboard:', shareableLink);
        
        toast({
          title: "Success",
          description: "Instant session link copied to clipboard! Redirecting to classroom...",
        });
      } catch (clipboardError) {
        console.warn('Failed to copy to clipboard:', clipboardError);
        toast({
          title: "Success", 
          description: "Instant session created! Redirecting to classroom...",
        });
      }

      // Small delay to let user see the toast, then navigate
      setTimeout(() => {
        navigate(`/classroom?session=${sessionId}&type=instant&role=tutor`);
      }, 1000);

      return {
        sessionId,
        shareableLink
      };
    } catch (error: any) {
      console.error('Error creating instant session:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create instant session",
        variant: "destructive"
      });
      return null;
    } finally {
      setIsCreating(false);
    }
  };

  const createScheduledSession = async (title: string, description: string, scheduledTime: Date) => {
    if (!user || user.role !== 'tutor') {
      toast({
        title: "Error",
        description: "Only tutors can create scheduled sessions",
        variant: "destructive"
      });
      return null;
    }

    setIsCreating(true);
    try {
      const sessionId = 'scheduled-' + Date.now() + '-' + Math.random().toString(36).substring(2, 9);
      
      console.log('Creating scheduled session with ID:', sessionId);

      const shareableLink = `${window.location.origin}/classroom?session=${sessionId}&type=scheduled&role=tutor`;

      toast({
        title: "Success",
        description: "Scheduled session created! Link copied to clipboard.",
      });

      try {
        await navigator.clipboard.writeText(shareableLink);
        console.log('Scheduled session link copied to clipboard:', shareableLink);
      } catch (clipboardError) {
        console.warn('Failed to copy to clipboard:', clipboardError);
      }

      return {
        sessionId,
        shareableLink,
        scheduledTime
      };
    } catch (error: any) {
      console.error('Error creating scheduled session:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create scheduled session",
        variant: "destructive"
      });
      return null;
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createInstantSession,
    createScheduledSession,
    isCreating
  };
};
